import {PerformanceMetric} from '@/api/User/useGetPlayerDetails';
import {SessionData} from '@/api/User/useGetPlayerSession';
import {getTargetValuesFromLevelAndFocus, TargetMetric} from '@/constants/v3TargetMetrics';
import {baselineBenchmarkValues, ExerciseBenchmark} from '@/pages/Baseline/Benchmarks/BaselineBenchmarks';
import clsx from 'clsx';
import {memo, ReactNode, useState} from 'react';
import Chevron from '../../../assets/img/icons/redesign-icons/chevron-down.svg?react';

export const MobileBaselineSessionTable = memo(
  ({
    exercises,
    baselineLevel,
    isMls = false,
    isV3Session = false,
  }: {
    exercises?: SessionData['exercises'];
    baselineLevel?: string;
    isMls?: boolean;
    isV3Session?: boolean;
  }) => {
    if (!exercises) {
      return null;
    }

    if (isV3Session) {
      // V3 Session logic - use existing implementation
      return (
        <>
          {exercises.map((exercise, i) => {
            return exercise ? (
              <_ExerciseSection
                key={`benchmark_${i}`}
                targetMetric={getTargetValuesFromLevelAndFocus(baselineLevel || '', exercise.focus || '')}
                exercise={exercise}
                defaultOpen={true}
                isMls={isMls}
                isV3Session={true}
              />
            ) : null;
          })}
        </>
      );
    } else {
      // Toca remote sessions logic - use benchmark values
      const benchmarks = baselineBenchmarkValues;
      const levelBenchmark = benchmarks.find((benchmark) => benchmark.level === (baselineLevel || 'beginner'));

      if (!levelBenchmark) {
        return null;
      }

      return (
        <>
          {levelBenchmark?.benchmarks.map((benchmark, i) => {
            const exercise = exercises?.[i];
            return exercise ? (
              <_ExerciseSection
                key={`benchmark_${i}`}
                targetMetric={benchmark}
                exercise={exercise}
                defaultOpen={true}
                isMls={isMls}
                isV3Session={false}
              />
            ) : null;
          })}
        </>
      );
    }
  }
);
MobileBaselineSessionTable.displayName = 'MobileBaselineSessionTable';

const _ExerciseSection = ({
  targetMetric,
  exercise,
  defaultOpen = false,
  isMls = false,
  isV3Session = false,
}: {
  targetMetric?: TargetMetric | ExerciseBenchmark;
  exercise: SessionData['exercises'][number] | PerformanceMetric;
  defaultOpen?: boolean;
  isMls?: boolean;
  isV3Session?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultOpen);

  const exerciseName = isV3Session
    ? (exercise as SessionData['exercises'][number]).focus
      ? `${
          (exercise as SessionData['exercises'][number]).focus!.charAt(0).toUpperCase() +
          (exercise as SessionData['exercises'][number]).focus!.slice(1)
        }/${(exercise as SessionData['exercises'][number]).name}`
      : (exercise as SessionData['exercises'][number]).name
    : (targetMetric as ExerciseBenchmark).title;

  return (
    <div
      data-test-id={`exercise-section-${exerciseName}`}
      className={`${
        isExpanded ? 'max-h-[600px]' : 'max-h-[48px]'
      } w-full overflow-hidden transition-all border border-grey-light border-b-0 last-of-type:border-b lg:last-of-type:rounded-b`}
    >
      <button
        onClick={() => setIsExpanded((prev) => !prev)}
        className="h-[48px] w-full px-6 py-4 bg-white flex justify-end items-center"
      >
        <span
          className="grow text-base text-left font-semibold overflow-hidden whitespace-nowrap text-ellipsis text-tertiary"
          data-test-id="exercise-section-title"
        >
          {exerciseName}
        </span>
        <Chevron className={`${isExpanded ? 'rotate-180' : ''} w-4 h-4 fill-grey-xdark opacity-75 transition-all`} />
      </button>

      <div className={`border-t ${isMls ? 'border-mls-grey-dark' : 'border-grey-light'}`}>
        <_SessionTable targetMetric={targetMetric!} exercise={exercise} isMls={isMls} isV3Session={isV3Session} />
      </div>
    </div>
  );
};

const _SessionTable = memo(
  ({
    targetMetric,
    exercise,
    isMls = false,
    isV3Session = false,
  }: {
    targetMetric: TargetMetric | ExerciseBenchmark;
    exercise: SessionData['exercises'][number] | PerformanceMetric;
    isMls?: boolean;
    isV3Session?: boolean;
  }) => {
    if (isV3Session) {
      return (
        <>
          <table className="grid grid-cols-[minmax(min-content,1fr),minmax(min-content,1fr)]">
            <thead className="contents">
              <tr
                className={`contents text-xs sm:text-sm tracking-[-0.25px] ${
                  isMls ? 'text-mls-primary' : 'text-primary'
                }`}
              >
                <th className="font-semibold bg-mls-grey-dark bg-opacity-20">
                  <_Cell>Target Accuracy</_Cell>
                </th>
                <th className="font-normal bg-mls-grey">
                  <_Cell>Accuracy</_Cell>
                </th>
                <th className="row-start-3 font-semibold bg-mls-grey-dark bg-opacity-20">
                  <_Cell>Target SOP</_Cell>
                </th>
                <th className="row-start-3 font-normal bg-mls-grey">
                  <_Cell>SOP</_Cell>
                </th>
              </tr>
            </thead>

            <tbody className="contents">
              <tr className={`contents text-sm sm:text-base ${isMls ? 'text-mls-baseline' : 'text-primary'}`}>
                <td className="bg-mls-grey-dark bg-opacity-25">
                  <_Cell isMls={isMls} isMetricValue={true} isTargetMetric={true} data-test-id="accuracy-target">
                    {(targetMetric as TargetMetric)?.accuracy
                      ? `${(targetMetric as TargetMetric).accuracy * 100}%`
                      : '-'}
                  </_Cell>
                </td>
                <td className="bg-mls-grey">
                  <_Cell isMls={isMls} isMetricValue={true} data-test-id="accuracy-value">
                    {(exercise as SessionData['exercises'][number])?.accuracy
                      ? `${
                          isMls
                            ? `${((exercise as SessionData['exercises'][number])?.accuracy * 100)?.toFixed(2)}%`
                            : `${(exercise as SessionData['exercises'][number]).accuracy}%`
                        }`
                      : '-'}
                  </_Cell>
                </td>
                <td className="row-start-4 bg-mls-grey-dark bg-opacity-25">
                  <_Cell isMls={isMls} isMetricValue={true} isTargetMetric={true} data-test-id="sop-target">
                    {(targetMetric as TargetMetric)?.sop ?? '-'}
                  </_Cell>
                </td>
                <td className="row-start-4 bg-mls-grey">
                  <_Cell isMls={isMls} isMetricValue={true} data-test-id="sop-value">
                    {(exercise as SessionData['exercises'][number])?.speedOfPlay ?? '-'}
                  </_Cell>
                </td>
              </tr>
            </tbody>
          </table>
        </>
      );
    } else {
      return (
        <>
          <table className="grid grid-cols-[minmax(min-content,2fr),minmax(min-content,1fr),minmax(min-content,2fr),minmax(min-content,1fr)]">
            <thead className="contents">
              <tr
                className={`contents text-xs sm:text-sm tracking-[-0.25px] ${
                  isMls ? 'text-mls-primary' : 'text-primary'
                }`}
              >
                <th className="font-semibold bg-mls-grey-dark">
                  <_Cell>Target Streak</_Cell>
                </th>
                <th className="font-normal bg-mls-grey">
                  <_Cell>Streak</_Cell>
                </th>
                <th className="font-semibold bg-mls-grey-dark bg-opacity-20">
                  <_Cell>Target Accuracy</_Cell>
                </th>
                <th className="font-normal bg-mls-grey">
                  <_Cell>Accuracy</_Cell>
                </th>
                <th className="row-start-3 font-semibold bg-mls-grey-dark bg-opacity-20">
                  <_Cell>Target SOP</_Cell>
                </th>
                <th className="row-start-3 font-normal bg-mls-grey">
                  <_Cell>SOP</_Cell>
                </th>
                <th className="row-start-3 font-semibold bg-mls-grey-dark bg-opacity-20">
                  <_Cell>Target MPH</_Cell>
                </th>
                <th className="row-start-3 font-normal bg-mls-grey">
                  <_Cell>MPH</_Cell>
                </th>
              </tr>
            </thead>

            <tbody className="contents">
              <tr className={`contents text-sm sm:text-base ${isMls ? 'text-mls-baseline' : 'text-primary'}`}>
                <td className="bg-mls-grey-dark bg-opacity-25">
                  <_Cell isMls={isMls} isMetricValue={true} isTargetMetric={true} data-test-id="streak-target">
                    {(targetMetric as ExerciseBenchmark)?.streak ?? '-'}
                  </_Cell>
                </td>
                <td className="bg-mls-grey">
                  <_Cell isMls={isMls} isMetricValue={true} data-test-id="streak-value">
                    {(exercise as PerformanceMetric)?.streak ?? '-'}
                  </_Cell>
                </td>
                <td className="bg-mls-grey-dark bg-opacity-25">
                  <_Cell isMls={isMls} isMetricValue={true} isTargetMetric={true} data-test-id="accuracy-target">
                    {(targetMetric as ExerciseBenchmark)?.accuracy
                      ? `${(targetMetric as ExerciseBenchmark).accuracy * 100}%`
                      : '-'}
                  </_Cell>
                </td>
                <td className="bg-mls-grey">
                  <_Cell isMls={isMls} isMetricValue={true} data-test-id="accuracy-value">
                    {(exercise as PerformanceMetric)?.accuracy
                      ? `${
                          isMls
                            ? `${((exercise as PerformanceMetric)?.accuracy * 100)?.toFixed(2)}%`
                            : `${(exercise as PerformanceMetric).accuracy}%`
                        }`
                      : '-'}
                  </_Cell>
                </td>
                <td className="row-start-4 bg-mls-grey-dark bg-opacity-25">
                  <_Cell isMls={isMls} isMetricValue={true} isTargetMetric={true} data-test-id="sop-target">
                    {(targetMetric as ExerciseBenchmark)?.sop ?? '-'}
                  </_Cell>
                </td>
                <td className="row-start-4 bg-mls-grey">
                  <_Cell isMls={isMls} isMetricValue={true} data-test-id="sop-value">
                    {(exercise as PerformanceMetric)?.speedOfPlay ?? '-'}
                  </_Cell>
                </td>
                <td className="row-start-4 bg-mls-grey-dark bg-opacity-25">
                  <_Cell isMls={isMls} isMetricValue={true} isTargetMetric={true} data-test-id="mph-target">
                    {(targetMetric as ExerciseBenchmark)?.mph ?? '-'}
                  </_Cell>
                </td>
                <td className="row-start-4 bg-mls-grey">
                  <_Cell isMls={isMls} isMetricValue={true} data-test-id="mph-value">
                    {(exercise as PerformanceMetric)?.ballSpeed || (exercise as PerformanceMetric)?.mph || '-'}
                  </_Cell>
                </td>
              </tr>
            </tbody>
          </table>
        </>
      );
    }
  }
);
_SessionTable.displayName = '_SessionTable';

const _Cell = ({
  children,
  isMls = false,
  isMetricValue = false,
  isTargetMetric = false,
}: {
  children: ReactNode;
  isMls?: boolean;
  isMetricValue?: boolean;
  isTargetMetric?: boolean;
}) => {
  return (
    <p
      className={clsx('h-[48px] min-h-[48px] grid place-content-center uppercase p-1', {
        'font-teko text-lg': isMls && isMetricValue,
        'font-bold': isMls && isTargetMetric,
      })}
    >
      {children}
    </p>
  );
};
